import { useState } from 'react';
import { Play } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Reveal } from './Reveal';

export interface FeatureItem {
  icon: any;
  title: string;
  desc: string;
  image: string;
  video?: string;
}

interface FeaturesSectionProps {
  features: FeatureItem[];
}

export function FeaturesSection({ features }: FeaturesSectionProps) {
  const [activeVideo, setActiveVideo] = useState<FeatureItem | null>(null);

  return (
    <section id="features" className="container mx-auto px-6 mt-24 md:mt-32 scroll-mt-24">
      <div className="max-w-6xl mx-auto flex flex-col gap-16 md:gap-24">
        {features.map((f, i) => {
          const reversed = i % 2 === 1;
          const Icon = f.icon;
          const canPlay = !!f.video;
          return (
            <div
              key={f.title}
              className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${reversed ? 'md:[&>*:first-child]:order-2' : ''}`}
            >
              {/* Media */}
              <Reveal variant={reversed ? 'slide-left' : 'slide-right'} duration={800}>
                <button
                  type="button"
                  onClick={() => canPlay && setActiveVideo(f)}
                  disabled={!canPlay}
                  aria-label={canPlay ? `Play ${f.title} tutorial video` : f.title}
                  className={`group relative block w-full overflow-hidden rounded-2xl paper transition-all ${canPlay ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1' : 'cursor-default'}`}
                >
                  <div
                    className="aspect-[4/3] flex items-center justify-center p-4"
                    style={{ backgroundColor: '#f5f5f5' }}
                  >
                    <img
                      src={f.image}
                      alt={f.title}
                      className="max-w-[92%] max-h-[92%] object-contain rounded-md transition-transform duration-500 group-hover:scale-[1.02]"
                      loading="lazy"
                      width={640}
                      height={480}
                    />
                  </div>

                  {canPlay && (
                    <>
                      {/* Subtle overlay on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300" />

                      {/* Play badge */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div
                          className="flex items-center justify-center w-20 h-20 rounded-full shadow-2xl transition-all duration-300 group-hover:scale-110"
                          style={{ backgroundColor: 'rgba(29, 61, 52, 0.92)' }}
                        >
                          <Play className="w-8 h-8 text-white fill-white ml-1" />
                        </div>
                      </div>

                    </>
                  )}
                </button>
              </Reveal>

              {/* Text */}
              <Reveal variant={reversed ? 'slide-right' : 'slide-left'} delay={120} duration={800}>
                <div className="flex flex-col">
                  <h3 className="text-[clamp(1.875rem,3vw,3.25rem)] font-black mb-5 text-landing-green leading-[1.08] tracking-[-0.02em]">
                    {f.title}
                  </h3>
                  <p className="text-landing-green text-[clamp(1.0625rem,1.5vw,1.75rem)] leading-[1.7] lg:max-w-[560px]">
                    {f.desc}
                  </p>
                </div>
              </Reveal>
            </div>
          );
        })}
      </div>

      <Dialog open={!!activeVideo} onOpenChange={(open) => !open && setActiveVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden border-0 bg-black gap-0">
          <VisuallyHidden>
            <DialogTitle>{activeVideo?.title ?? 'Tutorial video'}</DialogTitle>
          </VisuallyHidden>
          {activeVideo?.video && (
            <video
              key={activeVideo.video}
              src={activeVideo.video}
              controls
              autoPlay
              playsInline
              className="w-full h-auto max-h-[80vh] bg-black"
            />
          )}
          {activeVideo && (
            <div className="px-6 py-4 bg-white">
              <h4 className="font-serif text-xl font-bold mb-1">{activeVideo.title}</h4>
              <p className="text-sm text-muted-foreground">{activeVideo.desc}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
